import { clsx } from "clsx";
import { type ReactElement, useEffect, useMemo, useState } from "react";
import { type Accept, useDropzone } from "react-dropzone";
import { type BaseFieldProps, Field, type WrappedFieldProps } from "redux-form";

import { ActionButton, ActionButtonSize, Callout, Collapse, TriggerButton, TriggerButtonSize } from "widgets";

import style from "../../../styles/callout.module.scss";
import { default as listStyle } from "@/modules/files/styles/upload.module.scss";
import inputStyle from "@/ui-modules/core/redux-form/styles/FormWidget.module.scss";

import { fieldIsInvalid } from "../../../app-utils/form.js";
import { type Dictionary } from "../../../app-utils/type.js";
import { type IFileUpload } from "../../../redux/modules/file-upload.js";
import { FileUploadTile } from "@/modules/files/components/FileUploadTile.js";
import { FormError } from "@/ui-modules/core/form/layout/FormError.js";
import { FormInputGroup } from "@/ui-modules/core/form/layout/FormGroup.js";
import { FormInputHint } from "@/ui-modules/core/form/layout/FormInputHint.tsx";
import { FormInputWidget } from "@/ui-modules/core/form/layout/FormInputWidget.js";
import { FormLabel } from "@/ui-modules/core/form/layout/FormLabel.js";

export interface ITextareaDropzoneProps extends Partial<WrappedFieldProps> {
  name?: any;
  type?: string; // 'text' | 'password' : Only relevant if componentClass is 'input'
  horizontalLayout?: boolean;
  className?: string;
  placeholder?: string;
  title?: string | ReactElement;
  handleChange?: () => void;
  handleUploadFile?: (file: File, formData: FormData) => void;
  abortFileupload?: (fileName: string) => void;
  fileUploads?: Dictionary<IFileUpload>;
  hotspotId?: string;
  onlyFiles?: boolean;
  singleFile?: boolean;
  accept?: Accept;
  dropzonePlaceholderMessage?: string;
}

export interface DroppedFile extends File {
  path: string;
}

/**
 * Textarea optionally turning into Dropzone field, to be used in context of redux-form
 */
export default function TextareaDropzone(props: ITextareaDropzoneProps) {
  const [isDropzoneActive, setDropZoneActive] = useState(props.onlyFiles || false);
  const [isFieldInvalid, setFieldInvalid] = useState(fieldIsInvalid(props));
  const [inputOrFiles, setInputOrFiles] = useState<DroppedFile[]>(props.input.value); // injected by Redux Form, retrieve previous file set on onDrop()
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const { getRootProps, getInputProps, inputRef, isDragActive, open } = useDropzone({
    onDrop,
    accept: props.accept,
  });

  // CSS classes
  const inputFieldClasses = clsx(`${props.className} textarea-input`, inputStyle.input_widget, {
    [inputStyle.input_error]: isFieldInvalid,
    "font-monospace": !isDropzoneActive,
  });
  const dropzoneClasses = clsx({
    "visible-dropzone": isDragActive || (isDropzoneActive && (inputOrFiles.length > 0 || props.onlyFiles)),
    "active-dropzone": isDragActive,
  });

  useEffect(() => {
    setDropZoneActive((props.input.value && Array.isArray(props.input.value)) || props.onlyFiles);
    setInputOrFiles(props.input.value);
  }, [props.input.value]);

  useEffect(() => {
    setFieldInvalid(fieldIsInvalid(props));
  }, [props.meta]); // fieldIsInvalid is only checking the props.meta

  const error: string[] = useMemo(() => {
    return props.meta.error instanceof Array ? props.meta.error : [props.meta.error];
  }, [props.meta.error]);

  function onDrop(files) {
    if (props.singleFile && files.length > 1) {
      files.splice(1, files.length - 1);
    }

    // Apparently we find duplicates only by file names.
    // This is also the key for deleting files, so deleting duplicates, delete them all
    // We can upload folders which contains 2 files with the same names if we remove the next LOCs
    // So we filter duplicates for uploaded files first
    // and as we remove one file, we show the full path on the FileDropzoneItem instead of simple the name.
    let filesToUpload: DroppedFile[] = [];

    for (const file of files) {
      if (filesToUpload.find((f) => f.name === file.name) == undefined) {
        filesToUpload.push(file);
      }
    }

    // additional async upload of the file, result will be stored in redux state
    filesToUpload.forEach((file) => {
      const formData: FormData = new FormData();

      formData.append(file.name, file);
      props.handleUploadFile(file, formData);
    });

    // if files are already existing in the drop zone, we merge
    if (!props.singleFile && inputOrFiles && Array.isArray(inputOrFiles)) {
      filesToUpload = inputOrFiles
        .filter((file) => filesToUpload.find((droppedFile) => droppedFile.name === file.name) === undefined)
        .concat(filesToUpload);
    }

    // redux-form
    props.input.onChange(filesToUpload);
  }

  function deleteFile(fileToDelete: { name: string }) {
    const newListOfFiles = inputOrFiles.filter((file) => fileToDelete.name !== file.name);
    props.input.onChange(newListOfFiles); // to update the redux-form state
    props.abortFileupload(fileToDelete.name); // to update the blast state
    inputRef.current.value = null;
  }

  return (
    <FormInputGroup horizontalLayout={props.horizontalLayout} style={{ minWidth: 560 }}>
      {props.title && <FormLabel hotspotId={props.hotspotId}>{props.title}</FormLabel>}

      <FormInputWidget>
        <div className={"textarea-dropzone-input-field"}>
          <FormInputHint align={"start"}>
            <ActionButton
              label={`Select and Import ${props.singleFile ? "File" : "Files"}`}
              onPress={open}
              size={ActionButtonSize.Small}
            />{" "}
            or drag and drop {props.singleFile ? "it" : "them"} in the area below.{" "}
            {!props.singleFile && <em>Each file must have a unique name.</em>}
          </FormInputHint>
          <div
            className={`dropzone-area ${dropzoneClasses}`}
            {...getRootProps({
              onClick: (event) => event.stopPropagation(),
            })}
          >
            <input {...getInputProps()} ref={inputRef} />
            <div className={`dropzone ${dropzoneClasses}`} key="dropzone">
              {(!inputOrFiles || inputOrFiles.length === 0) && (
                <div className="dz-default dz-message">
                  {props.dropzonePlaceholderMessage ?? `Drop ${props.singleFile ? "file" : "files"} here to upload`}
                </div>
              )}
              <div className={listStyle.list}>
                {isDropzoneActive &&
                  inputOrFiles.length > 0 &&
                  inputOrFiles.map((file: File, i) => (
                    <FileUploadTile
                      file={{ fileName: file.name, fileSize: file.size }}
                      errors={props.fileUploads[file.name]?.errors}
                      key={`${i}-${file.name}`}
                      uploadStatus={props.fileUploads[file.name]?.uploadStatus}
                      progress={props.fileUploads[file.name]?.progress}
                      cancelUploadRequest={(file) => deleteFile({ name: file.fileName })}
                      clearFile={(file) => deleteFile({ name: file.fileName })}
                    />
                  ))}
              </div>
            </div>
            <textarea
              {...props.input}
              className={inputFieldClasses}
              placeholder={props.placeholder}
              rows={10}
              onChange={(e) => {
                props.input.onChange(e);
                props.handleChange?.();
              }}
            />
          </div>
          {isFieldInvalid &&
            (error.length > 1 ? (
              <FormError>
                <div style={{ display: "flex" }}>
                  <div style={{ flex: 1 }}>{props.meta.error[0]}</div>
                  <TriggerButton
                    label={isExpanded ? "Hide all errors" : "Show all errors"}
                    onPress={() => setIsExpanded(!isExpanded)}
                    icon={isExpanded ? "unfold_less_double" : "unfold_more_double"}
                    size={TriggerButtonSize.Small}
                  />
                </div>
                <Collapse isExpanded={isExpanded}>
                  {error.slice(1).map((err, index) => (
                    <Callout key={`${index}-error`} className={clsx(style.oldError)}>
                      {err}
                    </Callout>
                  ))}
                </Collapse>
              </FormError>
            ) : (
              <FormError>{props.meta.error}</FormError>
            ))}
        </div>
      </FormInputWidget>
    </FormInputGroup>
  );
}

export function TextareaDropzoneReduxFormField(props: ITextareaDropzoneProps & BaseFieldProps) {
  return <Field {...props} />;
}
